import { FC } from 'react';
import { X } from 'lucide-react';
import { chipColor } from '../../colorUtils';
import styles from './PunchupTag.module.css';

export interface PunchupTagProps {
	label: string;
	/** Tag color as a 6-digit hex string (e.g. '#3B82F6'). Drives background tint, border, and text. */
	background?: string;
	/** If provided, renders a remove (×) button inside the chip. */
	onRemove?: () => void;
	/** If provided, the entire chip becomes a clickable button. Hover matches .punchupButtonOutline. */
	onClick?: () => void;
	/**
	 * Size variant. `default` is the chip used in tag-management surfaces
	 * (Event Tags accordion, ticket-tags field, etc.). `sm` shrinks font and
	 * padding for table-cell density — use for status / type / scanned-style
	 * categorical cells where the chip sits inside a tight row.
	 */
	size?: 'default' | 'sm';
	/** Optional accessible-only description; rendered as a `title` attribute on the chip for hover tooltip support. */
	title?: string;
}

export const PunchupTag: FC<PunchupTagProps> = ({
	label,
	background = '#FF5E2A',
	onRemove,
	onClick,
	size = 'default',
	title,
}) => {
	const chipStyle = { '--chip-color': chipColor(background) } as React.CSSProperties;
	const sizeClass = size === 'sm' ? styles.tagSmall : '';

	// Hybrid mode: both edit (onClick) and direct remove (onRemove) coexist.
	// <span> container avoids nested <button> elements (invalid HTML).
	if (onClick && onRemove) {
		return (
			<span
				className={[styles.tag, styles.tagClickable, sizeClass].filter(Boolean).join(' ')}
				style={chipStyle}
				title={title}
				data-size={size}
			>
				<span
					className={styles.labelButton}
					role="button"
					tabIndex={0}
					onClick={onClick}
					onKeyDown={(e) => {
						if (e.key === 'Enter' || e.key === ' ') onClick();
					}}
				>
					{label}
				</span>
				<button type="button" className={styles.remove} onClick={onRemove} aria-label={`Remove ${label}`}>
					<X size={18} strokeWidth={2} />
				</button>
			</span>
		);
	}

	if (onClick) {
		return (
			<button
				type="button"
				className={[styles.tag, styles.tagClickable, sizeClass].filter(Boolean).join(' ')}
				style={chipStyle}
				onClick={onClick}
				title={title}
				data-size={size}
			>
				<span className={styles.label}>{label}</span>
			</button>
		);
	}

	return (
		<span
			className={[styles.tag, sizeClass].filter(Boolean).join(' ')}
			style={chipStyle}
			title={title}
			data-size={size}
		>
			<span className={styles.label}>{label}</span>
			{onRemove && (
				<button type="button" className={styles.remove} onClick={onRemove} aria-label={`Remove ${label}`}>
					<X size={18} strokeWidth={2} />
				</button>
			)}
		</span>
	);
};
